import React, { useState, useEffect, useCallback } from 'react';
import ProductionRunCard from './ProductionRunCard';
import './ProductionPlansList.css';

const PLAN_STATUS_OPTIONS = [
    { value: 'planned', label: 'Planned' },
    { value: 'in_progress', label: 'In Progress' },
    { value: 'completed', label: 'Completed' },
    { value: 'cancelled', label: 'Cancelled' }
];

export default function ProductionPlansList({
                                                plans,
                                                onRefresh,
                                                onDelete,
                                                onUpdatePlanStatus,
                                                onUpdateRunStatus,
                                                formats = []
                                            }) {
    const [expandedPlans, setExpandedPlans] = useState(new Set());
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [planToDelete, setPlanToDelete] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const [openStatusPlanId, setOpenStatusPlanId] = useState(null);

    const togglePlan = (planId) => {
        const newExpanded = new Set(expandedPlans);
        if (newExpanded.has(planId)) {
            newExpanded.delete(planId);
        } else {
            newExpanded.add(planId);
        }
        setExpandedPlans(newExpanded);
    };

    const handleDeleteClick = (e, plan) => {
        e.stopPropagation();
        setPlanToDelete(plan);
        setShowDeleteModal(true);
    };

    const handleConfirmDelete = useCallback(async () => {
        if (!planToDelete || !onDelete) return;

        setIsDeleting(true);
        try {
            await onDelete(planToDelete.id, planToDelete.name);
            setShowDeleteModal(false);
            setPlanToDelete(null);
        } catch (error) {
            console.error('Delete failed:', error);
        } finally {
            setIsDeleting(false);
        }
    }, [planToDelete, onDelete]);

    const handleCancelDelete = useCallback(() => {
        setShowDeleteModal(false);
        setPlanToDelete(null);
    }, []);

    useEffect(() => {
        const handleKeyDown = (e) => {
            if (showDeleteModal) {
                if (e.key === 'Escape') {
                    handleCancelDelete();
                } else if (e.key === 'Enter' && !isDeleting) {
                    handleConfirmDelete();
                }
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [showDeleteModal, isDeleting, handleCancelDelete, handleConfirmDelete]);

    const getStatusBadgeClass = (status) => {
        const baseClass = 'status-badge';
        switch (status) {
            case 'planned':
                return `${baseClass} planned`;
            case 'in_progress':
                return `${baseClass} in-progress`;
            case 'completed':
                return `${baseClass} completed`;
            case 'cancelled':
                return `${baseClass} cancelled`;
            default:
                return baseClass;
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'Not set';
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    const calculatePlanTotals = (plan) => {
        const planRuns = plan.production_runs || [];
        const totalPlanned = planRuns.reduce((sum, run) => sum + (run.planned_quantity || 0), 0);
        const totalProduced = planRuns.reduce((sum, run) => sum + (run.produced_quantity || 0), 0);
        return { totalPlanned, totalProduced };
    };

    const handlePlanStatusClick = (e, planId) => {
        e.stopPropagation();
        setOpenStatusPlanId(prev => (prev === planId ? null : planId));
    };

    const handlePlanStatusBlur = () => {
        setTimeout(() => setOpenStatusPlanId(null), 200);
    };

    const handlePlanStatusChange = (e, plan, statusValue) => {
        e.stopPropagation();
        if (statusValue !== plan.status && onUpdatePlanStatus) {
            onUpdatePlanStatus(plan.id, statusValue);
        }
        setOpenStatusPlanId(null);
    };

    if (plans.length === 0) {
        return (
            <div className="empty-state">
                <h3>No production plans found</h3>
                <p>There are currently no production plans matching your criteria.</p>
                <button onClick={onRefresh} className="btn btn-primary">
                    Refresh
                </button>
            </div>
        );
    }

    return (
        <>
            <div className="production-plans-list">
                {plans.map((plan) => {
                    const isExpanded = expandedPlans.has(plan.id);
                    const planRuns = plan.production_runs || [];
                    const { totalPlanned, totalProduced } = calculatePlanTotals(plan);
                    const completionPercentage = totalPlanned > 0 ? (totalProduced / totalPlanned) * 100 : 0;

                    return (
                        <div key={plan.id} className="production-plan-card">
                            <div
                                className="plan-header"
                                onClick={() => togglePlan(plan.id)}
                            >
                                <div className="plan-info">
                                    <div className="plan-title">
                                        <h3>{plan.name}</h3>
                                        <div className="plan-status-dropdown">
                                            <button
                                                type="button"
                                                className={getStatusBadgeClass(plan.status)}
                                                onClick={(e) => handlePlanStatusClick(e, plan.id)}
                                                onBlur={handlePlanStatusBlur}
                                            >
                                                {plan.status.replace('_', ' ').toUpperCase()}
                                                <span className="plan-status-arrow">▾</span>
                                            </button>
                                            {openStatusPlanId === plan.id && (
                                                <div className="plan-status-menu">
                                                    {PLAN_STATUS_OPTIONS.map((opt) => (
                                                        <div
                                                            key={opt.value}
                                                            className={`plan-status-item ${plan.status === opt.value ? 'active' : ''}`}
                                                            onClick={(e) => handlePlanStatusChange(e, plan, opt.value)}
                                                        >
                                                            {opt.label}
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                    {plan.description && (
                                        <p className="plan-description">{plan.description}</p>
                                    )}
                                    <div className="plan-meta">
                                        <span>Planned: {formatDate(plan.planned_start_date)} - {formatDate(plan.planned_end_date)}</span>
                                        {plan.actual_start_date && (
                                            <span>Actual: {formatDate(plan.actual_start_date)} - {formatDate(plan.actual_end_date)}</span>
                                        )}
                                    </div>
                                </div>

                                <div className="plan-summary">
                                    <div className="summary-item">
                                        <label>Runs</label>
                                        <span>{planRuns.length}</span>
                                    </div>
                                    <div className="summary-item">
                                        <label>Planned Cans</label>
                                        <span>{totalPlanned.toLocaleString()}</span>
                                    </div>
                                    <div className="summary-item">
                                        <label>Produced Cans</label>
                                        <span>{totalProduced.toLocaleString()}</span>
                                    </div>
                                    <div className="summary-item">
                                        <label>Progress</label>
                                        <span>{completionPercentage.toFixed(1)}%</span>
                                    </div>
                                    <button
                                        className="delete-plan-btn"
                                        onClick={(e) => handleDeleteClick(e, plan)}
                                        title="Delete production plan"
                                        aria-label={`Delete production plan ${plan.name}`}
                                    >
                                        <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                                            <path d="M3.75 4.5H14.25M7.5 8.25V12.75M10.5 8.25V12.75M4.5 4.5L5.25 14.25C5.25 14.6478 5.40804 15.0294 5.68934 15.3107C5.97064 15.592 6.35218 15.75 6.75 15.75H11.25C11.6478 15.75 12.0294 15.592 12.3107 15.3107C12.592 15.0294 12.75 14.6478 12.75 14.25L13.5 4.5M6.75 4.5V3C6.75 2.80109 6.82902 2.61032 6.96967 2.46967C7.11032 2.32902 7.30109 2.25 7.5 2.25H10.5C10.6989 2.25 10.8897 2.32902 11.0303 2.46967C11.171 2.61032 11.25 2.80109 11.25 3V4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                        </svg>
                                    </button>
                                    <div className="expand-icon">
                                        <span className={isExpanded ? 'expanded' : ''}>▾</span>
                                    </div>
                                </div>
                            </div>

                            {isExpanded && (
                                <div className="plan-details">
                                    {plan.notes && (
                                        <div className="plan-notes">
                                            <h4>Notes</h4>
                                            <p>{plan.notes}</p>
                                        </div>
                                    )}

                                    <div className="production-runs">
                                        <h4>Production Runs ({planRuns.length})</h4>
                                        {planRuns.length === 0 ? (
                                            <p className="no-runs">No production runs scheduled for this plan.</p>
                                        ) : (
                                            <div className="runs-list">
                                                {planRuns.map((run) => (
                                                    <ProductionRunCard
                                                        key={run.id}
                                                        run={run}
                                                        onRunStatusUpdated={onUpdateRunStatus}
                                                        formats={formats}
                                                        onRefresh={onRefresh}
                                                    />
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>

            {showDeleteModal && planToDelete && (
                <>
                    <div className="modal-overlay" onClick={handleCancelDelete} />
                    <div className="delete-confirmation-modal">
                        <div className="modal-header">
                            <h3>Delete Production Plan</h3>
                            <button
                                type="button"
                                className="modal-close"
                                onClick={handleCancelDelete}
                                disabled={isDeleting}
                                aria-label="Close modal"
                            >
                                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                                    <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                                </svg>
                            </button>
                        </div>
                        <div className="modal-body">
                            <div className="warning-icon">
                                <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
                                    <circle cx="24" cy="24" r="22" stroke="#ef4444" strokeWidth="2" fill="#fef2f2"/>
                                    <path d="M24 14V26M24 32V34" stroke="#ef4444" strokeWidth="3" strokeLinecap="round"/>
                                </svg>
                            </div>
                            <p className="modal-text">
                                Are you sure you want to delete the production plan <strong>"{planToDelete.name}"</strong>?
                            </p>
                            <p className="modal-subtext">
                                This action cannot be undone. All associated production runs and data will be permanently removed.
                            </p>
                            {planToDelete.production_runs && planToDelete.production_runs.length > 0 && (
                                <div className="modal-warning">
                                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                        <path d="M8 1L1 14H15L8 1Z" stroke="#f59e0b" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                        <path d="M8 6V9M8 11.5V12" stroke="#f59e0b" strokeWidth="1.5" strokeLinecap="round"/>
                                    </svg>
                                    <span>This plan has {planToDelete.production_runs.length} production run{planToDelete.production_runs.length !== 1 ? 's' : ''} that will also be deleted.</span>
                                </div>
                            )}
                        </div>
                        <div className="modal-footer">
                            <button
                                type="button"
                                className="modal-btn modal-btn-cancel"
                                onClick={handleCancelDelete}
                                disabled={isDeleting}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                className="modal-btn modal-btn-delete"
                                onClick={handleConfirmDelete}
                                disabled={isDeleting}
                            >
                                {isDeleting ? (
                                    <>
                                        <span className="spinner"></span>
                                        Deleting...
                                    </>
                                ) : (
                                    'Delete Plan'
                                )}
                            </button>
                        </div>
                    </div>
                </>
            )}
        </>
    );
}
